import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faUniversity, faCheckCircle, faExclamationTriangle, faInfoCircle, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

const { rootUrl } = require('../../../config/config');

const CashbookGLMapping = () => {
    const [accounts, setAccounts] = useState([]);
    const [coa, setCoa] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [toast, setToast] = useState(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const headers = { Authorization: `Bearer ${token}` };

            const [accountsRes, coaRes] = await Promise.all([
                axios.get(`${rootUrl}/api/cashbook/accounts`, { headers }),
                axios.get(`${rootUrl}/system/coa/accounts`, { headers })
            ]);

            setAccounts(accountsRes.data?.data || accountsRes.data || []);
            
            const coaData = coaRes.data?.data || [];
            const coaOptions = coaData.map(a => ({
                value: a.account_code,
                label: `${a.account_code} - ${a.account_name} (${a.account_type})`
            }));
            setCoa(coaOptions);
        } catch (error) {
            console.error("Error fetching mapping data:", error);
            showToast("error", "Failed to load mapping data");
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchData();
    }, []);

    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    };

    const handleUpdateMapping = async (accountId, glAccountCode) => {
        try {
            setSaving(accountId);
            const token = localStorage.getItem('authToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');

            await axios.patch(`${rootUrl}/api/cashbook/accounts/${accountId}`, {
                gl_account_code: glAccountCode,
                edit_by: user.username || 'system'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAccounts(accounts.map(acc => 
                acc.id === accountId ? { ...acc, gl_account_code: glAccountCode } : acc
            ));

            showToast("success", "Mapping updated successfully");
        } catch (error) {
            console.error("Error updating mapping:", error);
            showToast("error", error.response?.data?.message || "Failed to update mapping");
        } finally {
            setSaving(null);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl">
            <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-[#FF5733] mb-4" />
            <span className="text-sm font-medium text-gray-500 tracking-wide">Syncing account structures...</span>
        </div>
    );

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Contextual Info */}
            <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 bg-blue-50 border border-blue-100 p-6 rounded-2xl flex items-start gap-4">
                    <div className="p-3 bg-blue-100 rounded-xl text-blue-600">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-xl" />
                    </div>
                    <div>
                        <h3 className="font-bold text-blue-900 mb-1">General Ledger Integration</h3>
                        <p className="text-sm text-blue-800 leading-relaxed opacity-80">
                            Connect your operational accounts to the Chart of Accounts. 
                            When expenses are posted to the GL, the system uses these mappings to identify the correct credit accounts for double-entry bookkeeping.
                        </p>
                    </div>
                </div>
            </div>

            {/* Mappings Table */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100">Source Account</th>
                                <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100">Account Type</th>
                                <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faArrowRight} className="text-[#FF5733]" />
                                        Linked GL Account (Credit)
                                    </div>
                                </th>
                                <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100 text-center">Integration Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {accounts.map(account => (
                                <tr key={account.id} className="hover:bg-gray-25 transition-colors">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-gray-100 rounded-lg text-gray-400">
                                                <FontAwesomeIcon icon={faUniversity} />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-800">{account.account_name}</div>
                                                <div className="text-[10px] text-gray-400 font-mono tracking-tighter uppercase">{account.bank_name || 'Internal'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className={`px-2 py-1 rounded text-[10px] font-black tracking-tight ${
                                            account.account_type === 'BANK' ? 'bg-blue-100 text-blue-700' :
                                            account.account_type === 'INCOME' ? 'bg-green-100 text-green-700' :
                                            'bg-orange-100 text-orange-700'
                                        }`}>
                                            {account.account_type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 min-w-[400px]">
                                        <Select
                                            options={coa}
                                            placeholder="Assign GL Account..."
                                            value={coa.find(c => c.value === account.gl_account_code)}
                                            onChange={(option) => handleUpdateMapping(account.id, option ? option.value : null)}
                                            isClearable
                                            isLoading={saving === account.id}
                                            isDisabled={saving !== null && saving !== account.id}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderRadius: '12px',
                                                    borderColor: account.gl_account_code ? '#10b981' : (state.isFocused ? '#FF5733' : '#f3f4f6'),
                                                    padding: '2px',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    boxShadow: 'none',
                                                    backgroundColor: state.isDisabled ? '#f9fafb' : '#fff',
                                                    '&:hover': { borderColor: account.gl_account_code ? '#10b981' : '#FF5733' }
                                                })
                                            }}
                                        />
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {account.gl_account_code ? (
                                            <div className="flex flex-col items-center">
                                                <div className="p-1.5 bg-green-50 rounded-full mb-1">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-xs" />
                                                </div>
                                                <span className="text-[8px] text-green-600 font-black uppercase tracking-widest">Connected</span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center animate-pulse">
                                                <div className="p-1.5 bg-orange-50 rounded-full mb-1">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-400 text-xs" />
                                                </div>
                                                <span className="text-[8px] text-orange-600 font-black uppercase tracking-widest">Required</span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Toast Notifications */}
            {toast && (
                <div className={`fixed bottom-8 right-8 z-[9999] px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 animate-slide-up ${
                    toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                }`}>
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} className="text-xl" />
                    <div className="font-bold text-sm">{toast.message}</div>
                </div>
            )}
        </div>
    );
};

export default CashbookGLMapping;
